import { PREVIEW_STATE_DOT, PREVIEW_STATE_LABEL, type PreviewState } from '../lib/previewState';

/**
 * SPEC.md §3, §9 — the 30px segmented plotter readout:
 *
 *   state │ diagram type │ line count │ ⟨spacer⟩ │ render ms │ mermaid version
 *
 * Everything except the state is unknown until M1 wires the render loop, so a
 * null segment reads as an em dash rather than collapsing — the readout must
 * not change width as values arrive.
 *
 * M4.5 (issue #19): the spacer between line count and render time doubles as
 * a one-line notice slot — a session's first ZenUML render fills it with
 * "Loading ZenUML plugin" while the plugin loads, then it goes back to being
 * an empty spacer. It's otherwise unused space, so this needs no new segment
 * and no layout shift. M5.2 (issue #22) reuses the same slot for export/copy
 * confirmations ("Saved SVG."); App.tsx is what decides which of the two
 * sources wins if both are ever set at once — this component just renders
 * whatever single string it's given.
 */

interface StatusBarProps {
  state: PreviewState;
  /**
   * The detected diagram type's label; `'Unrecognised'` (§4) when the source
   * has content but matches no registry entry; null only when there's
   * nothing typed yet (App.tsx tells these two "no label" cases apart —
   * this component just renders whatever string it's given, em dash for
   * null).
   */
  diagramType: string | null;
  lineCount: number;
  /** Duration of the last completed render, in ms. */
  renderMs: number | null;
  mermaidVersion: string | null;
  /** A one-line, mid-render status message (e.g. a plugin loading); null the rest of the time. */
  notice: string | null;
}

function Segment({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex h-full items-center border-l border-ink-3 px-3 first:border-l-0">
      {children}
    </span>
  );
}

export default function StatusBar({
  state,
  diagramType,
  lineCount,
  renderMs,
  mermaidVersion,
  notice,
}: StatusBarProps) {
  return (
    <footer className="flex items-stretch border-t border-ink-3 bg-ink-2 text-[11px] text-mute">
      <Segment>
        <span className={`mr-1.5 leading-none ${PREVIEW_STATE_DOT[state]}`} aria-hidden>
          ●
        </span>
        <span className="text-text-ink">{PREVIEW_STATE_LABEL[state]}</span>
      </Segment>

      <Segment>{diagramType ?? '—'}</Segment>
      <Segment>{lineCount === 1 ? '1 line' : `${lineCount} lines`}</Segment>

      <span className="flex grow items-center border-l border-ink-3 px-3 text-text-ink" role="status">
        {notice}
      </span>

      <Segment>{renderMs === null ? '—' : `${renderMs}ms`}</Segment>
      <Segment>{mermaidVersion ?? '—'}</Segment>
    </footer>
  );
}
